import './styles.scss';

import { ReactElement, useEffect } from 'react';
import { formatNumber, generateQueryString, handleError, parseQueryString } from '@/utils';

import { ImportStatusEnum } from '@/models/gen/graphql';
import { LoadingBlur } from '@/components/LoadingSpinner';
import ManifestFilters from '@/features/Manifests/components/ManifestFilters';
import ManifestOverridesModal from '@/components/ManifestOverridesModal';
import ManifestTable from '@/features/Manifests/components/ManifestTable';
import ManifestUpload from '@/components/ManifestUpload';
import { ManifestsTypes } from '@/features/Manifests/types';
import PageInfo from '@/components/PageInfo';
import { createPersistentComponentState } from '@/state';
import { customIndexedDBStorage } from '@/state/storage';
import updateImportsStatus from '@/api/services/imports/updateImportsStatus';
import { useNavigate } from 'react-router-dom';

export const useManifestsState = createPersistentComponentState<ManifestsTypes.State>('ManifestsState', ManifestsTypes.initState, {
  storage: customIndexedDBStorage,
});

const Manifests = (): ReactElement => {
  const [{ imports, total, loading, manifestUpload, manifestOverrides }, setState] = useManifestsState(({ state, setState }) => [
    {
      imports: state.imports,
      total: state.total,
      loading: state.loading,
      manifestUpload: state.modals.manifestUpload,
      manifestOverrides: state.modals.manifestOverrides,
    },
    setState,
  ]);

  const currentImports = formatNumber(imports?.length || 0);
  const totalImports = formatNumber(total || 0);

  const onManifestUploadSuccess = async (uploads, files, emailAddress) => {
    try {
      if (!files?.length) return;
      await parseFilesAndUpdateStatus(uploads, files, emailAddress);
      const { id, name } = files?.[0] || {};
      setState((current: any): any => ({ ...current, importing: false }));
      if (files.length !== 1) return;
      setState((current) => ({
        ...current,
        modals: {
          ...current.modals,
          manifestUpload: { show: false },
          manifestOverrides: {
            show: true,
            manifestId: id,
            name,
            status: ImportStatusEnum.Parsed,
          },
        },
      }));
    } catch (err) {
      handleError(err, { notification: { title: 'Manifest Upload' } });
    }
  };

  const parseFilesAndUpdateStatus = async (_uploads, files, emailAddress) => {
    if (!files?.length) return;
    const ids = [];
    for (let u = 0; u < files.length; u++) {
      const { id } = files[u];
      if (!id) continue;
      ids.push(id);
    }
    await updateImportsStatus(ids, ImportStatusEnum.Uploaded, emailAddress);
  };

  const navigate = useNavigate();

  const handleSubmitManifestOverrideModal = (id: string, keepDriversAndVehicles: boolean) => {
    setState((current) => ({
      ...current,
      modals: {
        ...current.modals,
        manifestOverrides: {
          show: false,
        },
      },
    }));
    navigate(`/manifests/${id}?${generateQueryString({ ...parseQueryString(window.location.search), keepDriversAndVehicles })}`);
  };

  // Hide both modals when the page unmounts
  useEffect(() => {
    return () => {
      setState((current: any): any => ({
        ...current,
        modals: {
          ...current.modals,
          manifestUpload: { show: false },
          manifestOverrides: { show: false },
        },
      }));
    };
    /* IGNORE DEPS
      we only want this to fire on unmount
    */ // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <ManifestFilters />
      <PageInfo>
        Imports: {currentImports} / {totalImports}
      </PageInfo>
      <LoadingBlur loading={loading} />
      <ManifestTable />
      <ManifestUpload
        show={manifestUpload?.show}
        onHide={(): void =>
          setState((current: any): any => ({ ...current, modals: { ...current.modals, manifestUpload: { show: false } } }))
        }
        onSuccess={onManifestUploadSuccess}
      />
      <ManifestOverridesModal
        show={manifestOverrides?.show}
        onHide={(): void =>
          setState((current: any): any => ({ ...current, modals: { ...current.modals, manifestOverrides: { show: false } } }))
        }
        importId={manifestOverrides?.manifestId}
        name={manifestOverrides?.name}
        onSubmit={handleSubmitManifestOverrideModal}
      />
    </>
  );
};

export default Manifests;
